// ASCII render engine for AsciiField. Pure functions, no React.
// Each frame fills three same-size character arrays: [faint, muted, mint].
/* eslint-disable no-var */
var RAMP = " .:-=+*#%";
function fib(n) {
  var pts = [], g = Math.PI * (3 - Math.sqrt(5));
  for (var i = 0; i < n; i++) {
    var y = 1 - (i + 0.5) / n * 2, r = Math.sqrt(1 - y * y), t = g * i * 7.3;
    pts.push([Math.cos(t) * r, y, Math.sin(t) * r]);
  }
  return pts;
}
function nearest(pts, k) {
  var edges = [];
  for (var i = 0; i < pts.length; i++) {
    var d = [];
    for (var j = 0; j < pts.length; j++) if (j !== i) {
      var dx = pts[i][0] - pts[j][0], dy = pts[i][1] - pts[j][1], dz = pts[i][2] - pts[j][2];
      d.push([dx * dx + dy * dy + dz * dz, j]);
    }
    d.sort(function (a, b) { return a[0] - b[0]; });
    for (var m = 0; m < k; m++) if (d[m][1] > i) edges.push([i, d[m][1]]);
  }
  return edges;
}
// Deterministic 2D hash in [0,1), used for both the sphere's dissolve effect
// and each node's fixed "explosion" jitter (no Math.random: must be stable
// frame to frame and identical on every mount).
function hash2(x, y) {
  var v = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return v - Math.floor(v);
}

export function makeGlobe(cols, rows, nodeCount) {
  var nodes = fib(nodeCount), edges = nearest(nodes, 2);
  // A denser neighbor graph than `edges`, minus what's already in it: extra
  // connections that fade in as the field is hovered, so the sphere reads as
  // "expanding" into its underlying knowledge graph rather than just zooming.
  var edgeKey = {}, e2;
  for (e2 = 0; e2 < edges.length; e2++) edgeKey[edges[e2][0] + ":" + edges[e2][1]] = true;
  var dense = nearest(nodes, 6), extraEdges = [];
  for (e2 = 0; e2 < dense.length; e2++) {
    var k2 = dense[e2][0] + ":" + dense[e2][1];
    if (!edgeKey[k2]) extraEdges.push(dense[e2]);
  }
  // Per-node outward "explosion" factor, fixed at creation so the burst is
  // uneven (organic graph spread) but always reproducible.
  var jitter = nodes.map(function (_, i) { return 0.4 + hash2(i * 3.1, i * 7.9) * 1.0; });

  var ASP = 1.9, s0 = Math.min(cols / ASP, rows) / 2 * 0.86, cx0 = cols / 2, cy0 = rows / 2;
  var L = [-0.55, -0.6, 0.58];
  return function frame(t, out, boost) {
    boost = boost || 0;
    var s = s0 * (1 - boost * 0.2);
    var faint = out[0], ink = out[1], mint = out[2], edge = out[3], n = cols * rows, i;
    for (i = 0; i < n; i++) { faint[i] = " "; ink[i] = " "; mint[i] = " "; edge[i] = " "; }
    // Spin picks up while boosted, so the burst feels energetic rather than
    // just a static rearrangement.
    var a = t * (0.35 + boost * 0.55), ca = Math.cos(a), sa = Math.sin(a), tl = 0.38, ct = Math.cos(tl), st = Math.sin(tl);
    function rot(p) {
      var x = p[0] * ca + p[2] * sa, z = -p[0] * sa + p[2] * ca, y = p[1];
      return [x, y * ct - z * st, y * st + z * ct];
    }
    function put(layer, p, ch, front) {
      var px = Math.round(cx0 + p[0] * s * ASP - 0.5), py = Math.round(cy0 + p[1] * s - 0.5);
      if (px < 0 || py < 0 || px >= cols || py >= rows) return -1;
      var k = py * cols + px;
      if (front === false && (p[0] * p[0] + p[1] * p[1]) < 1 && p[2] < 0) return -1;
      layer[k] = ch; return k;
    }
    // Sphere shell: dissolves away per-pixel as boost rises (fixed threshold
    // per cell, so the same pixels drop out first every time) rather than
    // vanishing all at once, like it's crumbling into the graph underneath.
    if (boost < 0.999) {
      for (var y = 0; y < rows; y++) for (var x = 0; x < cols; x++) {
        if (boost > 0 && hash2(x, y) < boost) continue;
        var u = (x + 0.5 - cx0) / ASP / s, v = (y + 0.5 - cy0) / s, rr = u * u + v * v;
        if (rr >= 1) continue;
        var z = Math.sqrt(1 - rr);
        // inverse-rotate the view point to sphere space
        var z1 = -v * st + z * ct;
        var x2 = u * ca - z1 * sa, z2 = u * sa + z1 * ca;
        var lon = Math.atan2(z2, x2);
        var b = 0.12 + 0.88 * Math.max(0, u * L[0] + v * L[1] + z * L[2]);
        var k = y * cols + x;
        var mer = Math.abs(((lon * 180 / Math.PI) + 360) % 30 - 15) > 13.2;
        var idx = Math.min(RAMP.length - 1, Math.floor(b * (RAMP.length - 0.01)) + (mer ? 2 : 0));
        if (idx < 1) idx = 1;
        if (b > 0.5 || mer) ink[k] = RAMP.charAt(idx); else faint[k] = RAMP.charAt(idx);
      }
    }
    // orbit ring + satellite: a tidy circular orbit reads wrong once the
    // graph has burst outward, so both shrink away with boost.
    var ringN = Math.round(140 * (1 - boost));
    for (var o = 0; o < ringN; o++) {
      var th = o / 140 * Math.PI * 2, p = [Math.cos(th) * 1.32, 0.02, Math.sin(th) * 1.32];
      var q = [p[0], p[1] * Math.cos(-0.5) - p[2] * Math.sin(-0.5), p[1] * Math.sin(-0.5) + p[2] * Math.cos(-0.5)];
      put(faint, q, ".", false);
    }
    if (boost < 0.7) {
      var sth = t * 0.9, sp = [Math.cos(sth) * 1.32, 0.02, Math.sin(sth) * 1.32];
      var sq = [sp[0], sp[1] * Math.cos(-0.5) - sp[2] * Math.sin(-0.5), sp[1] * Math.sin(-0.5) + sp[2] * Math.cos(-0.5)];
      put(mint, sq, "o", false);
    }
    // knowledge graph: nodes burst outward from their sphere position and
    // gently wobble as boost rises, independent per node so it reads as a
    // graph settling into a loose layout rather than a uniform inflation.
    var R = nodes.map(function (p, i2) {
      var r = rot(p);
      var grow = 1 + boost * jitter[i2];
      var wob = boost * 0.07, wt = t * (0.8 + (i2 % 5) * 0.11) + i2 * 2.3;
      return [
        r[0] * grow + Math.sin(wt) * wob,
        r[1] * grow + Math.cos(wt * 1.3) * wob,
        r[2] * grow + Math.sin(wt * 0.6) * wob,
      ];
    });
    function drawEdges(list) {
      for (var e = 0; e < list.length; e++) {
        var A = R[list[e][0]], B = R[list[e][1]];
        if (A[2] < 0.05 || B[2] < 0.05) continue;
        var lenA = Math.sqrt(A[0] * A[0] + A[1] * A[1] + A[2] * A[2]);
        var lenB = Math.sqrt(B[0] * B[0] + B[1] * B[1] + B[2] * B[2]);
        var segs = 14;
        for (var st2 = 1; st2 < segs; st2++) {
          var f = st2 / segs;
          var mx = A[0] + (B[0] - A[0]) * f, my = A[1] + (B[1] - A[1]) * f, mz = A[2] + (B[2] - A[2]) * f;
          var mlen = Math.sqrt(mx * mx + my * my + mz * mz) || 1;
          // At boost=0 this pulls the midpoint onto the sphere surface (a
          // great-circle-looking arc, the original look). At boost=1 it's a
          // plain straight line between the exploded endpoints, like a real
          // graph edge. In between, a smooth blend of the two.
          var targetLen = lenA + (lenB - lenA) * f;
          var scale = 1 + (targetLen / mlen - 1) * (1 - boost);
          var m = [mx * scale, my * scale, mz * scale];
          var kk = put(edge, m, "·", true); if (kk >= 0) { ink[kk] = " "; faint[kk] = " "; mint[kk] = " "; }
        }
      }
    }
    drawEdges(edges);
    if (boost > 0.02) drawEdges(extraEdges.slice(0, Math.ceil(extraEdges.length * boost)));
    var brightCut = 0.6 - boost * 0.3;
    for (var nn = 0; nn < R.length; nn++) {
      if (R[nn][2] < 0.08 - boost * 0.15) continue;
      var kn = put(mint, R[nn], R[nn][2] > brightCut ? "@" : "o", true);
      if (kn >= 0) { ink[kn] = " "; faint[kn] = " "; edge[kn] = " "; }
    }
  };
}
export function makeWave(cols, rows) {
  return function frame(t, out) {
    var faint = out[0], ink = out[1], mint = out[2];
    for (var y = 0; y < rows; y++) for (var x = 0; x < cols; x++) {
      var k = y * cols + x;
      var v = (Math.sin(x * 0.11 + t * 0.9) + Math.sin(y * 0.35 - t * 0.6) + Math.sin((x + y * 2) * 0.05 + t * 0.4)) / 3;
      var b = (v + 1) / 2;
      faint[k] = " "; ink[k] = " "; mint[k] = " ";
      if (b > 0.86) mint[k] = b > 0.93 ? "#" : "+";
      else if (b > 0.62) ink[k] = RAMP.charAt(Math.floor((b - 0.62) / 0.24 * 4) + 5);
      else faint[k] = RAMP.charAt(Math.floor(b / 0.62 * 5));
    }
  };
}

// Focus: a field of scattered noise that tightens into one sharp ring, holds, then drifts loose again.
// Cell aspect is ~1:2, so x distances are halved to keep the ring round.
export function makeFocus(cols, rows) {
  var cx = (cols - 1) / 2, cy = (rows - 1) / 2, R = rows * 0.34;
  return function frame(t, out) {
    var faint = out[0], ink = out[1], mint = out[2];
    var f = 0.5 - 0.5 * Math.cos(t * 0.5);            // 0 scattered, 1 locked
    f = f * f * (3 - 2 * f);
    var w = 4.5 - 3.9 * f, tick = Math.floor(t * 5);
    for (var y = 0; y < rows; y++) for (var x = 0; x < cols; x++) {
      var k = y * cols + x, dx = (x - cx) * 0.5, dy = y - cy;
      var r = Math.sqrt(dx * dx + dy * dy), d = (r - R) / w;
      var ring = Math.exp(-d * d);
      var n = hash2(x + tick * 3.1, y + tick * 1.7);
      var b = ring * (0.3 + 0.7 * f) * (1 - (1 - f) * 0.9 * n) + n * (1 - f) * 0.2 * (r < R * 1.6 ? 1 : 0.35);
      if (f > 0.9 && r < 0.9) b = 1;                      // pinpoint at the centre once locked
      faint[k] = " "; ink[k] = " "; mint[k] = " ";
      if (b > 0.8) mint[k] = b > 0.92 ? "#" : "+";
      else if (b > 0.45) ink[k] = RAMP.charAt(Math.floor((b - 0.45) / 0.35 * 4) + 5);
      else if (b > 0.06) faint[k] = RAMP.charAt(Math.floor(b / 0.45 * 4) + 1);
    }
  };
}

// Block-letter "graspd" (figlet ANSI Shadow).
export const BANNER = " \u2588\u2588\u2588\u2588\u2588\u2588\u2557 \u2588\u2588\u2588\u2588\u2588\u2588\u2557  \u2588\u2588\u2588\u2588\u2588\u2557 \u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2557\u2588\u2588\u2588\u2588\u2588\u2588\u2557 \u2588\u2588\u2588\u2588\u2588\u2588\u2557\n\u2588\u2588\u2554\u2550\u2550\u2550\u2550\u255d \u2588\u2588\u2554\u2550\u2550\u2588\u2588\u2557\u2588\u2588\u2554\u2550\u2550\u2588\u2588\u2557\u2588\u2588\u2554\u2550\u2550\u2550\u2550\u255d\u2588\u2588\u2554\u2550\u2550\u2588\u2588\u2557\u2588\u2588\u2554\u2550\u2550\u2588\u2588\u2557\n\u2588\u2588\u2551  \u2588\u2588\u2588\u2557\u2588\u2588\u2588\u2588\u2588\u2588\u2554\u255d\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2551\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2557\u2588\u2588\u2588\u2588\u2588\u2588\u2554\u255d\u2588\u2588\u2551  \u2588\u2588\u2551\n\u2588\u2588\u2551   \u2588\u2588\u2551\u2588\u2588\u2554\u2550\u2550\u2588\u2588\u2557\u2588\u2588\u2554\u2550\u2550\u2588\u2588\u2551\u255a\u2550\u2550\u2550\u2550\u2588\u2588\u2551\u2588\u2588\u2554\u2550\u2550\u2550\u255d \u2588\u2588\u2551  \u2588\u2588\u2551\n\u255a\u2588\u2588\u2588\u2588\u2588\u2588\u2554\u255d\u2588\u2588\u2551  \u2588\u2588\u2551\u2588\u2588\u2551  \u2588\u2588\u2551\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2551\u2588\u2588\u2551     \u2588\u2588\u2588\u2588\u2588\u2588\u2554\u255d\n \u255a\u2550\u2550\u2550\u2550\u2550\u255d \u255a\u2550\u255d  \u255a\u2550\u255d\u255a\u2550\u255d  \u255a\u2550\u255d\u255a\u2550\u2550\u2550\u2550\u2550\u2550\u255d\u255a\u2550\u255d     \u255a\u2550\u2550\u2550\u2550\u2550\u255d";
